import {
  AdminReservationSummary,
  GetUserReservationsResponse,
  ReservationSourceStatus,
} from 'tools/bff/generated/bff/admin/admin_user_pb';
import { Alert, Box, DataTable, IconLogoResy, IconLogoTock, Loader, Text } from '@tocktix/pantry';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { formatReservationDate, formatReservationTime } from 'tools/users/dateFormatters';
import { userClient } from 'tools/bff/client';
import StatusTag, { StatusDisplay } from 'tools/users/StatusTag';

type ReservationHistorySectionProps = {
  tockPatronId?: bigint;
  resyUserId?: bigint;
  enabled: boolean;
};

const reservationStatusDisplay: Record<string, StatusDisplay> = {
  BOOKED: { label: 'Booked', color: 'success' },
  SEATED: { label: 'Seated', color: 'primary' },
  LEFT: { label: 'Left', color: 'success' },
  CANCELED: { label: 'Canceled', color: 'warning' },
  NO_SHOW: { label: 'No Show', color: 'danger' },
};

const reservationSourceDisplay: Record<string, StatusDisplay> = {
  TOCK: { label: 'TOCK', color: 'primary', IconStart: IconLogoTock },
  RESY: { label: 'RESY', color: 'danger', IconStart: IconLogoResy },
};

const ReservationHistorySection: FunctionComponent<ReservationHistorySectionProps> = ({
  tockPatronId,
  resyUserId,
  enabled,
}) => {
  const [reservationSources, setReservationSources] = useState<GetUserReservationsResponse | null>(
    null
  );
  const [reservationsLoading, setReservationsLoading] = useState(false);
  const [reservationSort, setReservationSort] = useState<{
    key: string | null;
    direction?: 'asc' | 'desc';
  }>({
    key: 'date',
    direction: 'desc',
  });
  const hasFetchedRef = useRef(false);

  const tockFailed =
    reservationSources?.tockReservations?.status === ReservationSourceStatus.FAILED;
  const resyFailed =
    reservationSources?.resyReservations?.status === ReservationSourceStatus.FAILED;

  const mergedReservations: (AdminReservationSummary & { source: 'TOCK' | 'RESY' })[] = useMemo(
    () => [
      ...(reservationSources?.tockReservations?.reservations ?? []).map((reservation) => ({
        ...reservation,
        source: 'TOCK' as const,
      })),
      ...(reservationSources?.resyReservations?.reservations ?? []).map((reservation) => ({
        ...reservation,
        source: 'RESY' as const,
      })),
    ],
    [
      reservationSources?.resyReservations?.reservations,
      reservationSources?.tockReservations?.reservations,
    ]
  );

  const sortedReservations = useMemo(
    () =>
      [...mergedReservations].sort((a, b) => {
        if (reservationSort.key === 'date') {
          const cmp = (a.startDateTime ?? '').localeCompare(b.startDateTime ?? '');
          return reservationSort.direction === 'asc' ? cmp : -cmp;
        }
        return 0;
      }),
    [mergedReservations, reservationSort]
  );

  // Fetch reservations once either tockPatronId or resyUserId is known from the profile.
  useEffect(() => {
    if (!enabled || (!tockPatronId && !resyUserId) || hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    let isMounted = true;
    const loadReservations = async () => {
      setReservationsLoading(true);
      try {
        const res = await userClient.getUserReservations({
          tockPatronId,
          resyUserId,
          limit: 10,
        });
        if (isMounted) {
          setReservationSources(res);
        }
      } catch {
        // Reservation fetch failure is non-fatal; leave reservations empty.
      } finally {
        if (isMounted) {
          setReservationsLoading(false);
        }
      }
    };

    loadReservations();
    return () => {
      isMounted = false;
    };
  }, [enabled, resyUserId, tockPatronId]);

  return (
    <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
      {reservationsLoading ? (
        <Box padding={300} display="flex" justifyContent="center">
          <Loader aria-label="reservations-loading" size="md" />
        </Box>
      ) : (
        <Box padding={300} display="flex" flexDirection="column" gap={200}>
          {tockFailed && (
            <Alert
              color="warning"
              message="Failed to fetch Tock reservations. Contact engineering if this persists."
              data-testid="reservation-source-error-tock"
            />
          )}
          {resyFailed && (
            <Alert
              color="warning"
              message="Failed to fetch Resy reservations. Contact engineering if this persists."
              data-testid="reservation-source-error-resy"
            />
          )}
          {sortedReservations.length === 0 ? (
            <Text variant="body" color="fgSubtle">
              No reservation history available.
            </Text>
          ) : (
            <DataTable
              id="reservation-history-table"
              sortable={{
                initialSort: { key: 'date', direction: 'desc' as const },
                onSort: (config) =>
                  setReservationSort({ key: config.key, direction: config.direction }),
                accessibilityLabels: {
                  tableIsSortableInstructions: 'Reservation history table, sortable',
                  currentSortedColumn: 'Currently sorted column',
                  asc: 'Sort ascending',
                  desc: 'Sort descending',
                },
              }}
            >
              <DataTable.HeaderRow>
                <DataTable.Header id="venue" title="Venue" />
                <DataTable.Header id="date" title="Date" sortable />
                <DataTable.Header id="time" title="Time" />
                <DataTable.Header id="partySize" title="Party Size" />
                <DataTable.Header id="status" title="Status" />
                <DataTable.Header
                  id="source"
                  title="Source"
                  data-testid="reservation-source-column-header"
                />
              </DataTable.HeaderRow>
              <DataTable.Body>
                {sortedReservations.map((res, index) => {
                  return (
                    <DataTable.Row key={index} id={index} index={index}>
                      <DataTable.Cell columnId="venue">{res.venueName ?? '\u2014'}</DataTable.Cell>
                      <DataTable.Cell columnId="date">
                        {formatReservationDate(res.startDateTime)}
                      </DataTable.Cell>
                      <DataTable.Cell columnId="time">
                        {formatReservationTime(res.startDateTime)}
                      </DataTable.Cell>
                      <DataTable.Cell columnId="partySize">
                        {res.partySize ?? '\u2014'}
                      </DataTable.Cell>
                      <DataTable.Cell columnId="status">
                        <StatusTag value={res.status} displayMap={reservationStatusDisplay} />
                      </DataTable.Cell>
                      <DataTable.Cell columnId="source">
                        <StatusTag
                          value={res.source}
                          displayMap={reservationSourceDisplay}
                          data-testid="reservation-source-tag"
                        />
                      </DataTable.Cell>
                    </DataTable.Row>
                  );
                })}
              </DataTable.Body>
            </DataTable>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReservationHistorySection;
